import { IconButton, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

interface MediaPopupProps {
  open: boolean;
  onClose: () => void;
  mediaUrl: string;
  mediaType: string;
  mediaName?: string;
  /** WhatsApp GIF (video/mp4 flagged isGif). Renders looping, muted,
   *  autoplaying and without controls — matching WhatsApp's viewer. */
  isGif?: boolean;
}

export default function MediaPopup({ open, onClose, mediaUrl, mediaType, mediaName, isGif }: MediaPopupProps) {
  const { t } = useTranslation(namespace);
  const isImage = mediaType?.startsWith('image/');
  const isVideo = mediaType?.startsWith('video/');
  const isPdf = mediaType?.startsWith('application/pdf');
  const pdfDocumentLabel = t('scheduledMessage.media.pdfDocument');

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent
        showCloseButton={false}
        className="w-auto max-w-[90vw] max-h-[90vh] p-0"
        style={{
          // Center in the viewport with inset:0 + margin:auto rather than the
          // shared DialogContent's Tailwind `fixed`/`translate-1/2` utilities.
          // Those don't resolve in this render context (they left the media
          // flowing into the document, oversized and pushed below the fold),
          // and the %+translate approach mis-centers here. inset:0 + margin
          // auto + fit-content sizing is self-contained and robust. Inline
          // styles win over the classes; `padding:0` kills the base sm:p-6.
          position: 'fixed',
          inset: 0,
          margin: 'auto',
          width: 'fit-content',
          height: 'fit-content',
          maxWidth: '90vw',
          maxHeight: '90vh',
          padding: 0,
          // Kill BOTH transform-based and standalone-`translate`-property
          // -50% shifts from the shared DialogContent's Tailwind classes —
          // otherwise inset/margin centering ends up half-a-box off.
          transform: 'none',
          translate: 'none',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/* Header bar for PDFs to avoid conflicts with PDF controls */}
        {isPdf && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '48px',
              backgroundColor: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
              zIndex: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: 'white',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                marginRight: 2,
              }}
            >
              {mediaName || pdfDocumentLabel}
            </Typography>
            <IconButton
              onClick={onClose}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}

        {/* Standard close button for non-PDF media */}
        {!isPdf && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              backgroundColor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.7)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        )}

        {isImage && (
          <img
            src={mediaUrl}
            alt={mediaName || ''}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        )}

        {isVideo && (
          <video
            controls={!isGif}
            autoPlay={isGif}
            loop={isGif}
            muted={isGif}
            playsInline
            preload="metadata"
            src={mediaUrl}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        )}

        {isPdf && (
          <iframe
            src={mediaUrl}
            title={mediaName || pdfDocumentLabel}
            style={{
              width: '90vw',
              height: 'calc(90vh - 48px)',
              border: 'none',
              display: 'block',
              marginTop: '48px'
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
